import { AlertCircle, Trash2 } from "lucide-react";
import { FieldArrayWithId, useFormContext } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import { CustomToolTip } from "@/components/reusables/CustomToolTip";
import SingleFileUpload from "@/components/reusables/SingleFileUpload";
import DateInput from "@/components/reusables/custom-form-fields/DateInput";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { TemployeeWorkInfoSchema } from "@/schemas/employee-schema";

interface DeclarationTrainingsProps {
  selectedTrainings: FieldArrayWithId<
    TemployeeWorkInfoSchema,
    "previousExperience.trainings",
    "id"
  >[];
  trainings: FieldArrayWithId<TemployeeWorkInfoSchema, "trainings", "id">[];
  handleRemoveTraining: (index: number) => void;
}

const DeclarationTrainings = (
  props: DeclarationTrainingsProps,
): JSX.Element => {
  const form = useFormContext();
  const { selectedTrainings, handleRemoveTraining } = props;

  if (!selectedTrainings?.length) {
    return <></>;
  }

  const isWarning = (index: number): boolean => {
    const expiryDate = new Date(form.watch(`trainings.${index}.expiryDate`));
    const today = new Date();
    // Set the time of today to 00:00:00 to exclude today from the comparison
    today.setHours(0, 0, 0, 0);

    return today > expiryDate;
  };

  return (
    <div className='space-y-4'>
      {selectedTrainings.map((training, index) => {
        const date = new Date(form.watch(`trainings.${index}.issuedDate`));
        const addOneDay = new Date(
          date.setTime(date.getTime() + 24 * 60 * 60 * 1000),
        );
        const trainingImage = form.watch(`trainings.${index}.image`);

        return (
          <div className='p-4 space-y-4 border rounded-md' key={training.value}>
            <div className='flex justify-between'>
              <h2>{training.label}</h2>
              <ButtonWithIcon
                icon={<Trash2 size={18} className='' />}
                size={"icon"}
                variant={"lightDestructive"}
                onClick={() => handleRemoveTraining(index)}
              />
            </div>
            <section className='grid grid-cols-3 gap-x-4'>
              <FormInput
                form={form}
                name={`trainings.${index}.issuedDate`}
                label='Issued Date'
                required
                render={(field) => {
                  return (
                    <DateInput
                      onChange={(value) => {
                        form.setValue(`trainings.${index}.issuedDate`, value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        form.setValue(
                          `trainings.${index}.name`,
                          training.label,
                        );
                      }}
                      defaultValue={field.value as Date}
                      validateDate={(date) => date > new Date()}
                    />
                  );
                }}
              />
              <FormInput
                form={form}
                name={`trainings.${index}.expiryDate`}
                label='Expiry Date'
                {...(isWarning(index) && {
                  warningMsg: {
                    node: (
                      <CustomToolTip
                        toolTipContent={"Ticket has expired"}
                        toolTipTrigger={
                          <AlertCircle className='w-4 h-4 text-red-500' />
                        }
                      />
                    ),
                    position: "top",
                  },
                })}
                render={(field) => {
                  return (
                    <DateInput
                      onChange={field.onChange}
                      defaultValue={field.value as Date}
                      dateLimit={addOneDay}
                      disabled={!form.watch(`trainings.${index}.issuedDate`)}
                    />
                  );
                }}
              />
              <FormInput
                form={form}
                name={`trainings.${index}.image`}
                label='Upload File'
                required
                render={(field) => (
                  <SingleFileUpload
                    {...field}
                    files={trainingImage || []}
                    setFiles={(files) => {
                      form.setValue(`trainings.${index}.image`, files, {
                        shouldDirty: true,
                      });
                    }}
                    type='input'
                    acceptsMixedContent
                  />
                )}
              />
            </section>
          </div>
        );
      })}
    </div>
  );
};

export default DeclarationTrainings;
